import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X, Check, Clock, MessageCircle, CalendarX } from "lucide-react";
import { patchBooking } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { waDigits, confirmMsg, declineMsg, waCustomer, fmtTime12 } from "../lib/bookingActions";

const STATUS_COLORS = {
  pending: "#b0863f",
  confirmed: "#3f9d63",
  declined: "#e08a8a",
  completed: "#c98a63",
};
const STATUS_LABEL = { pending: "Pending", confirmed: "Confirmed", declined: "Cancelled", completed: "Completed" };
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const dateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function monthWeeks(year, month) {
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const weeks = [];
  let week = new Array(startOffset).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(new Date(year, month, d));
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }
  return weeks;
}

export default function BookingsCalendar({ bookings, refresh }) {
  const { toast } = useToast();
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selected, setSelected] = useState(null); // YYYY-MM-DD
  const [busyId, setBusyId] = useState(null);

  const byDate = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (!b.date) continue;
      (map[b.date] = map[b.date] || []).push(b);
    }
    Object.values(map).forEach((list) => list.sort((a, b) => (a.time || "").localeCompare(b.time || "")));
    return map;
  }, [bookings]);

  const act = async (b, patch, okMsg) => {
    setBusyId(b.id);
    try {
      await patchBooking(b.id, patch);
      if (refresh) await refresh();
      toast({ title: okMsg });
      return true;
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "Update failed" });
      return false;
    } finally {
      setBusyId(null);
    }
  };
  const accept = async (b) => {
    const ok = await act(b, { status: "confirmed" }, "Booking accepted");
    if (ok) window.open(waCustomer(b, confirmMsg(b)), "_blank");
  };
  const decline = async (b) => {
    const ok = await act(b, { status: "declined" }, "Booking cancelled");
    if (ok) window.open(waCustomer(b, declineMsg(b)), "_blank");
  };

  const weeks = monthWeeks(cursor.y, cursor.m);
  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
  const todayKey = dateKey(today);
  const selectedList = selected ? byDate[selected] || [] : [];
  const unscheduled = bookings.filter((b) => !b.date);

  const prev = () => setCursor((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }));
  const next = () => setCursor((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }));
  const goToday = () => setCursor({ y: today.getFullYear(), m: today.getMonth() });

  const chip = (b) => (
    <div
      key={b.id}
      className="truncate text-[10px] leading-tight px-1.5 py-0.5 rounded text-white"
      style={{ background: STATUS_COLORS[b.status] || "#b0863f" }}
      title={`${b.time ? fmtTime12(b.time) : ""} ${b.name} - ${b.service}`}
    >
      {b.time ? `${fmtTime12(b.time)} ` : ""}{b.name}
    </div>
  );

  return (
    <div data-testid="bookings-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-display text-2xl text-cream">Calendar</h2>
        <div className="flex items-center gap-2">
          <button onClick={prev} data-testid="cal-prev" aria-label="Previous month" className="w-9 h-9 grid place-items-center rounded-lg border hairline text-cream/70 hover:text-gold">
            <ChevronLeft size={16} />
          </button>
          <div className="font-display text-lg text-cream min-w-[150px] text-center" data-testid="cal-month-label">{monthLabel}</div>
          <button onClick={next} data-testid="cal-next" aria-label="Next month" className="w-9 h-9 grid place-items-center rounded-lg border hairline text-cream/70 hover:text-gold">
            <ChevronRight size={16} />
          </button>
          <button onClick={goToday} data-testid="cal-today" className="text-xs px-3 py-2 rounded-lg border hairline text-cream/70 hover:text-gold">
            Today
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4">
        {Object.keys(STATUS_COLORS).map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5 text-xs text-cream/60">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[s] }} /> {STATUS_LABEL[s]}
          </span>
        ))}
      </div>

      <div className="rounded-2xl border hairline bg-viimii-2 overflow-hidden">
        <div className="grid grid-cols-7 border-b hairline">
          {WEEKDAYS.map((d) => (
            <div key={d} className="text-center text-[11px] uppercase tracking-wider text-cream/45 py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {weeks.map((week, wi) =>
            week.map((day, di) => {
              const key = day ? dateKey(day) : `empty-${wi}-${di}`;
              const list = day ? byDate[dateKey(day)] || [] : [];
              const isToday = day && dateKey(day) === todayKey;
              const isSelected = day && selected === dateKey(day);
              return (
                <button
                  key={key}
                  disabled={!day}
                  onClick={() => day && setSelected(dateKey(day))}
                  data-testid={day ? `cal-day-${dateKey(day)}` : undefined}
                  className={`min-h-[72px] md:min-h-[96px] p-1.5 border-b border-r hairline text-left align-top transition-colors ${day ? "hover:bg-viimii cursor-pointer" : "opacity-40 cursor-default"} ${isSelected ? "bg-viimii" : ""}`}
                  style={isSelected ? { boxShadow: "inset 0 0 0 2px var(--viimii-gold)" } : {}}
                >
                  {day && (
                    <>
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className={`text-xs font-medium w-6 h-6 grid place-items-center rounded-full ${isToday ? "text-black" : "text-cream/70"}`}
                          style={isToday ? { background: "var(--viimii-gold)" } : {}}
                        >
                          {day.getDate()}
                        </span>
                        {list.length > 0 && (
                          <span className="text-[10px] px-1.5 rounded-full text-cream/60 border hairline">{list.length}</span>
                        )}
                      </div>
                      <div className="space-y-0.5">
                        {list.slice(0, 3).map(chip)}
                        {list.length > 3 && (
                          <div className="text-[10px] text-cream/50 px-1">+{list.length - 3} more</div>
                        )}
                      </div>
                    </>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>

      {unscheduled.length > 0 && (
        <div className="mt-5 p-4 rounded-2xl border hairline bg-viimii-2" data-testid="cal-unscheduled">
          <p className="text-sm text-cream/70 flex items-center gap-2 mb-2">
            <CalendarX size={15} style={{ color: "var(--viimii-gold)" }} /> {unscheduled.length} booking(s) without a date
          </p>
          <div className="flex flex-wrap gap-2">
            {unscheduled.map((b) => (
              <span key={b.id} className="text-xs px-3 py-1.5 rounded-full border hairline text-cream/70">
                {b.name} · {b.service}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Day detail modal */}
      {selected && (
        <div className="fixed inset-0 z-[80] grid place-items-center p-4" data-testid="cal-day-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-md bg-viimii-2 border hairline rounded-[24px] p-6 max-h-[85vh] overflow-y-auto">
            <button onClick={() => setSelected(null)} className="absolute top-4 right-4 text-cream/60 hover:text-cream" aria-label="Close">
              <X size={20} />
            </button>
            <p className="section-line text-gold mb-1">Bookings</p>
            <h3 className="font-display text-2xl text-cream mb-4">
              {new Date(selected + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </h3>
            {selectedList.length === 0 && <p className="text-cream/50 text-sm">No bookings on this day.</p>}
            <div className="space-y-3">
              {selectedList.map((b) => (
                <div key={b.id} data-testid="cal-day-booking" className="p-4 rounded-xl border hairline bg-viimii">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="font-display text-lg text-cream">{b.name}</h4>
                    <span className="text-[11px] px-2.5 py-1 rounded-full text-white" style={{ background: STATUS_COLORS[b.status] || "#b0863f" }}>
                      {STATUS_LABEL[b.status] || b.status}
                    </span>
                  </div>
                  <p className="text-sm text-cream/65 mt-1">{b.service}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-sm text-cream/60">
                    {b.time && <span className="inline-flex items-center gap-1"><Clock size={13} style={{ color: "var(--viimii-gold)" }} /> {fmtTime12(b.time)}{b.duration ? ` · ${b.duration}m` : ""}</span>}
                    {b.amount ? <span style={{ color: "var(--viimii-gold)" }}>₹{Number(b.amount).toLocaleString("en-IN")}</span> : null}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-cream/45">{b.phone}</span>
                    <a
                      href={`https://wa.me/${waDigits(b.phone)}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="cal-day-whatsapp"
                      aria-label={`WhatsApp ${b.name}`}
                      className="inline-flex items-center gap-1.5 text-xs text-white px-3 py-1.5 rounded-full hover:opacity-90"
                      style={{ background: "#25D366" }}
                    >
                      <MessageCircle size={13} /> WhatsApp
                    </a>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t hairline">
                    {b.status !== "confirmed" && b.status !== "declined" && (
                      <button
                        onClick={() => accept(b)}
                        disabled={busyId === b.id}
                        data-testid="cal-day-accept"
                        className="inline-flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-lg text-black hover:opacity-90 disabled:opacity-60"
                        style={{ background: "#7fc99a" }}
                      >
                        <Check size={14} /> Accept &amp; Notify
                      </button>
                    )}
                    {b.status !== "declined" && (
                      <button
                        onClick={() => decline(b)}
                        disabled={busyId === b.id}
                        data-testid="cal-day-decline"
                        className="inline-flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-lg border hairline text-cream/80 hover:text-cream disabled:opacity-60"
                        style={{ borderColor: "rgba(224,138,138,0.4)" }}
                      >
                        <X size={14} /> Decline &amp; Notify
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
